



import React, { useState, useEffect, useCallback, useRef } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
// import MainHeader from "../../Navigation/MainHeader";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Eye, Pencil, Trash2, X, Plus, MapPin, 
  Search, ChevronLeft, ChevronRight, Layers, RotateCcw 
} from "lucide-react";

const Projects = () => {
  // Data & Loading States
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search Logic States
  const [inputValue, setInputValue] = useState(""); // What's typed in the box
  const [searchTerm, setSearchTerm] = useState(""); // Only updates on 'Search' button click
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [viewProject, setViewProject] = useState(null);
  const [errors, setErrors] = useState({});

  const initialFormState = { 
    projectName: "", 
    location: "", 
    description: "", 
    totalAreaAcres: "", 
    totalPlots: "", 
    projectStatus: "Upcoming" 
  };
  
  const [formData, setFormData] = useState(initialFormState);

  // 1. Fetch Suggestions for Dropdown (while typing)
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (inputValue.trim().length >= 3) {
        try {
          const res = await fetch(`${APIConfig.APIACTIVEURL}Project/GetSuggestions?term=${inputValue}`);
          const data = await res.json();
          setSuggestions(data);
          setShowDropdown(true);
        } catch (e) {
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    };
    fetchSuggestions();
  }, [inputValue]);

  // 2. Fetch Projects for Table
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.PROJECT_GET_ALL}?pageNumber=${currentPage}&pageSize=10&searchTerm=${searchTerm}`;
      const response = await fetch(url);
      const result = await response.json();
      setProjects(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // --- Search Handlers ---

  const handleSearchClick = () => {
    setSearchTerm(inputValue);
    setCurrentPage(1);
    setShowDropdown(false);
  };

  const handleClearSearch = () => {
    setInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
    setSuggestions([]);
    setShowDropdown(false);
  };

  const handleSuggestionClick = (val) => {
    setInputValue(val);
    setShowDropdown(false);
  };

  // --- CRUD Handlers ---

  const openModal = (project = null) => {
    setErrors({});
    if (project) {
      setEditingProject(project);
      setFormData({ ...project });
    } else {
      setEditingProject(null);
      setFormData(initialFormState);
    }
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: false });
  };

  const validateForm = () => {
    let tempErrors = {};
    if (!formData.projectName?.trim()) tempErrors.projectName = true;
    if (!formData.location?.trim()) tempErrors.location = true;
    if (!formData.totalAreaAcres || formData.totalAreaAcres <= 0) tempErrors.totalAreaAcres = true;
    if (!formData.totalPlots || formData.totalPlots < 0) tempErrors.totalPlots = true;
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const isEdit = !!editingProject;
    try {
      const res = await fetch(APIConfig.APIACTIVEURL + APIConfig.PROJECT_BASE, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setShowModal(false);
        fetchProjects();
        Swal.fire({ icon: 'success', title: isEdit ? 'Updated' : 'Created', timer: 1500, showConfirmButton: false });
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to save project.', 'error');
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "Permanent delete!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Yes, Delete'
    }).then(async (result) => {
      if (result.isConfirmed) {
        await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PROJECT_BASE}/${id}`, { method: "DELETE" });
        fetchProjects();
        Swal.fire('Deleted!', '', 'success');
      }
    });
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const styles = {
    container: { padding: "40px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    searchGroup: { display: "flex", gap: "10px", position: "relative", width: "480px" },
    input: { flex: 1, padding: "12px 45px 12px 40px", borderRadius: "12px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "14px", backgroundColor:'#fff' },
    clearBtn: { position: "absolute", right: "120px", top: "12px", border: "none", background: "none", cursor: "pointer", color: "#94a3b8" },
    dropdown: { position: "absolute", top: "55px", left: 0, width: "75%", backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 10px 25px rgba(0,0,0,0.1)", zIndex: 100, border: "1px solid #e2e8f0", overflow: "hidden" },
    suggestionItem: { padding: "12px 15px", cursor: "pointer", borderBottom: "1px solid #f1f5f9", fontSize: "14px", color: "#475569", display: "flex", alignItems: "center", gap: "10px" },
    searchBtn: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "0 25px", borderRadius: "12px", cursor: "pointer", fontWeight: "700", display:'flex', alignItems:'center', gap:'5px' },
    addBtn: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "12px 24px", borderRadius: "12px", cursor: "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" },
    card: { backgroundColor: "#fff", borderRadius: "20px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)", overflow: "hidden" },
    modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2000, backdropFilter: "blur(6px)" },
    modalContent: { backgroundColor: "#fff", padding: "35px", borderRadius: "24px", width: "550px", boxShadow: "0 25px 50px rgba(0,0,0,0.1)" },
    formInput: (hasError) => ({ width: '100%', padding: '12px', borderRadius: '10px', border: hasError ? '2px solid #ef4444' : '1.5px solid #e2e8f0', marginTop: '5px', outline: 'none', boxSizing: 'border-box' }),
    statusBadge: (status) => ({
        padding: "5px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "700",
        backgroundColor: status === "Ongoing" ? "#e6fffa" : status === "Upcoming" ? "#fffaf0" : "#f0f5ff",
        color: status === "Ongoing" ? "#059669" : status === "Upcoming" ? "#d97706" : "#2563eb"
    }),
  };

  return (
    <>
      {/* <MainHeader /> */}
       <Navbar /> 
      <div style={styles.container}>
        {/* Header / Search Area */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "35px" }}>
          <div>
            <h2 style={{ fontSize: "28px", fontWeight: "800", color: "#1b2559", margin: "0 0 15px 0" }}>Projects</h2>
            
            <div style={styles.searchGroup} ref={dropdownRef}>
              <Search size={18} style={{ position: "absolute", left: "15px", top: "14px", color: "#94a3b8" }} />
              <input 
                style={styles.input} 
                placeholder="Search projects..." 
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
              />
              
              {inputValue && (
                <button style={styles.clearBtn} onClick={handleClearSearch} title="Clear Search">
                  <X size={18} />
                </button>
              )}

              <button style={styles.searchBtn} onClick={handleSearchClick}>
                Search
              </button>

              {showDropdown && suggestions.length > 0 && (
                <div style={styles.dropdown}>
                  {suggestions.map((s, idx) => (
                    <div key={idx} style={styles.suggestionItem} onClick={() => handleSuggestionClick(s)}>
                      <Layers size={14} color="#6366f1" /> {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button style={styles.addBtn} onClick={() => openModal()}>
            <Plus size={18} /> Create New Project
          </button>
        </div>

        {/* Table Area */}
        <div style={styles.card}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc", color: "#a3aed0", fontSize: "12px", fontWeight: "700", textTransform: "uppercase" }}>
                <th style={{ padding: "18px 20px", textAlign: "left" }}>Project Identity</th>
                <th style={{ padding: "18px 20px", textAlign: "left" }}>Area</th>
                <th style={{ padding: "18px 20px", textAlign: "left" }}>Plots</th>
                <th style={{ padding: "18px 20px", textAlign: "left" }}>Status</th>
                <th style={{ padding: "18px 20px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ padding: "60px", textAlign: "center", color: "#a3aed0" }}>Synchronizing data...</td></tr>
              ) : projects.length === 0 ? (
                <tr><td colSpan="5" style={{ padding: "60px", textAlign: "center", color: "#a3aed0" }}>No projects found.</td></tr>
              ) : projects.map((p) => (
                <tr key={p.projectId} style={{ borderBottom: "1px solid #f4f7fe" }}>
                  <td style={{ padding: "20px" }}>
                    <div style={{ fontWeight: "700", color: "#1b2559", fontSize:'15px' }}>{p.projectName}</div>
                    <div style={{ fontSize: "12px", color: "#a3aed0", marginTop: "4px", display:'flex', alignItems:'center', gap:'4px' }}>
                        <MapPin size={12} /> {p.location}
                    </div>
                  </td>
                  <td style={{ padding: "20px", fontSize: "14px", color:'#1b2559', fontWeight:'500' }}>{p.totalAreaAcres} <span style={{fontSize:'10px', color:'#a3aed0'}}>ACRES</span></td>
                  <td style={{ padding: "20px", fontSize: "14px", color:'#1b2559', fontWeight:'500' }}>{p.totalPlots}</td>
                  <td style={{ padding: "20px" }}><span style={styles.statusBadge(p.projectStatus)}>{p.projectStatus}</span></td>
                  <td style={{ padding: "20px", textAlign: "center" }}>
                    <div style={{display:'flex', justifyContent:'center', gap:'10px'}}>
                        <button onClick={() => {setViewProject(p); setShowViewModal(true)}} style={{border:'none', background:'#eff6ff', color:'#3b82f6', padding:'8px', borderRadius:'8px', cursor:'pointer'}}><Eye size={18}/></button>
                        <button onClick={() => openModal(p)} style={{border:'none', background:'#eef2ff', color:'#6366f1', padding:'8px', borderRadius:'8px', cursor:'pointer'}}><Pencil size={18}/></button>
                        <button onClick={() => handleDelete(p.projectId)} style={{border:'none', background:'#fef2f2', color:'#ef4444', padding:'8px', borderRadius:'8px', cursor:'pointer'}}><Trash2 size={18}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Footer */}
          <div style={{ padding: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #f4f7fe" }}>
            <span style={{ fontSize: "14px", color: "#a3aed0" }}>Page <strong style={{color:'#1b2559'}}>{currentPage}</strong> of {totalPages}</span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={{ padding: "8px 15px", borderRadius: "10px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer" }}><ChevronLeft size={18}/></button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => c + 1)} style={{ padding: "8px 15px", borderRadius: "10px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer" }}><ChevronRight size={18}/></button>
            </div>
          </div>
        </div>

        {/* --- VIEW MODAL --- */}
        {showViewModal && viewProject && (
            <div style={styles.modalOverlay}>
                <div style={{...styles.modalContent, borderTop:'6px solid #4f46e5'}}>
                    <div style={{display:'flex', justifyContent:'space-between', marginBottom:'25px'}}>
                        <h3 style={{margin:0, color:'#1b2559', fontWeight:'800'}}>Project Details</h3>
                        <X size={22} style={{cursor:'pointer', color:'#a3aed0'}} onClick={() => setShowViewModal(false)} />
                    </div>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'25px'}}>
                        <div style={{gridColumn:'span 2'}}><small style={{color:'#a3aed0', fontWeight:'700'}}>PROJECT NAME</small><div style={{fontSize:'18px', fontWeight:'800', color:'#1b2559'}}>{viewProject.projectName}</div></div>
                        <div style={{gridColumn:'span 2'}}><small style={{color:'#a3aed0', fontWeight:'700'}}>LOCATION</small><div style={{fontWeight:'600', color:'#1b2559'}}>{viewProject.location}</div></div>
                        <div><small style={{color:'#a3aed0', fontWeight:'700'}}>TOTAL AREA</small><div style={{color:'#1b2559'}}>{viewProject.totalAreaAcres} Acres</div></div>
                        <div><small style={{color:'#a3aed0', fontWeight:'700'}}>PLOT COUNT</small><div style={{color:'#1b2559'}}>{viewProject.totalPlots} Units</div></div>
                        <div><small style={{color:'#a3aed0', fontWeight:'700'}}>STATUS</small><div style={{marginTop:'5px'}}><span style={styles.statusBadge(viewProject.projectStatus)}>{viewProject.projectStatus}</span></div></div>
                    </div>
                    <div style={{marginTop:'40px', textAlign:'right'}}><button onClick={() => setShowViewModal(false)} style={styles.addBtn}>Close Details</button></div>
                </div>
            </div>
        )}

        {/* --- ADD/EDIT MODAL --- */}
        {showModal && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={{display:'flex', justifyContent:'space-between', marginBottom:'25px'}}>
                 <h3 style={{margin:0, fontWeight:'800', color:'#1b2559'}}>{editingProject ? "Update Project" : "New Project"}</h3>
                 <X size={22} style={{cursor:'pointer', color:'#a3aed0'}} onClick={() => setShowModal(false)} />
              </div>
              <form onSubmit={handleSubmit}>
                <div style={{marginBottom:'20px'}}><label style={{fontSize:'14px', fontWeight:'700', color:'#1b2559'}}>Project Name</label>
                  <input name="projectName" placeholder="Enter name" style={styles.formInput(errors.projectName)} value={formData.projectName} onChange={handleInputChange} />
                </div>
                <div style={{marginBottom:'20px'}}><label style={{fontSize:'14px', fontWeight:'700', color:'#1b2559'}}>Location</label>
                  <input name="location" placeholder="Enter city/area" style={styles.formInput(errors.location)} value={formData.location} onChange={handleInputChange} />
                </div>
                
                {/* Spaced Acres and Plots */}
                <div style={{display:'flex', gap:'25px', marginBottom:'25px'}}>
                    <div style={{flex:1}}><label style={{fontSize:'14px', fontWeight:'700', color:'#1b2559'}}>Acres</label>
                        <input type="number" step="0.000001" name="totalAreaAcres" placeholder="Total Area" style={styles.formInput(errors.totalAreaAcres)} value={formData.totalAreaAcres} onChange={handleInputChange} />
                    </div>
                    <div style={{flex:1}}><label style={{fontSize:'14px', fontWeight:'700', color:'#1b2559'}}>Plots</label>
                        <input type="number" name="totalPlots" placeholder="Quantity" style={styles.formInput(errors.totalPlots)} value={formData.totalPlots} onChange={handleInputChange} />
                    </div>
                </div>

                <div style={{marginBottom:'35px'}}><label style={{fontSize:'14px', fontWeight:'700', color:'#1b2559'}}>Project Status</label>
                  <select name="projectStatus" value={formData.projectStatus} onChange={handleInputChange} style={styles.formInput(false)}>
                    <option value="Upcoming">Upcoming</option>
                    <option value="Ongoing">Ongoing</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                
                <div style={{display:'flex', justifyContent:'flex-end', gap:'15px'}}>
                  <button type="button" onClick={() => setShowModal(false)} style={{padding:'12px 25px', borderRadius:'12px', border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontWeight:'700', color:'#a3aed0'}}>Cancel</button>
                  <button type="submit" style={{...styles.addBtn, padding:'12px 35px'}}>Save Project</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Projects;